import { Link } from 'react-router-dom';
import { ArrowUpRight, CalendarDays, Clock } from 'lucide-react';
import { type BlogPost, useBlog } from '../../context/BlogContext';
import { getImageUrl } from '../../utils/imageUtils';

const formatPostDate = (date: string) => {
  const value = new Date(date);

  if (Number.isNaN(value.getTime())) {
    return '';
  }

  return value.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
};

const getPostPath = (post: BlogPost) => `/blog/${post.slug || post.id}`;

const PostImage = ({ post }: { post: BlogPost }) => {
  if (!post.image) {
    return (
      <div className="flex h-full w-full items-center justify-center bg-gradient-to-br from-primary/15 via-card to-accent/10">
        <span className="text-xs font-heading uppercase tracking-[0.24em] text-primary/70">
          Virtuoz
        </span>
      </div>
    );
  }

  return (
    <img
      src={getImageUrl(post.image)}
      alt={post.title}
      className="h-full w-full object-cover transition-all duration-700 ease-out-expo group-hover:scale-105"
      loading="lazy"
    />
  );
};

const PostMeta = ({ post }: { post: BlogPost }) => {
  const formattedDate = formatPostDate(post.date);

  return (
    <div className="flex flex-wrap items-center gap-x-4 gap-y-2 text-xs text-muted">
      {formattedDate && (
        <span className="inline-flex items-center gap-1.5">
          <CalendarDays className="h-3.5 w-3.5" />
          {formattedDate}
        </span>
      )}
      {post.readTime > 0 && (
        <span className="inline-flex items-center gap-1.5">
          <Clock className="h-3.5 w-3.5" />
          {post.readTime} min read
        </span>
      )}
    </div>
  );
};

const BlogPreviewSkeleton = () => (
  <div className="grid grid-cols-1 gap-3 lg:grid-cols-3">
    {[0, 1, 2].map((item) => (
      <div
        key={item}
        className="grid grid-cols-[112px_minmax(0,1fr)] gap-4 rounded-lg border border-foreground/5 bg-card/80 p-3 sm:grid-cols-[132px_minmax(0,1fr)] lg:grid-cols-1"
      >
        <div className="h-24 animate-pulse rounded bg-foreground/5 sm:h-28 lg:h-32" />
        <div className="space-y-3 py-1">
          <div className="h-3 w-20 animate-pulse rounded bg-foreground/10" />
          <div className="h-5 w-full animate-pulse rounded bg-foreground/10" />
          <div className="h-4 w-4/5 animate-pulse rounded bg-foreground/10" />
          <div className="h-3 w-28 animate-pulse rounded bg-foreground/10" />
        </div>
      </div>
    ))}
  </div>
);

const BlogPreview = () => {
  const { posts, loading } = useBlog();
  const latestPosts = posts.slice(0, 3);

  if (!loading && latestPosts.length === 0) return null;

  return (
    <section className="mb-16">
      <div className="mb-5 flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
        <div className="max-w-2xl">
          <h2 className="section-title mb-3">
            <span>Latest Articles</span>
          </h2>
          <p className="text-sm leading-relaxed text-muted">
            Practical notes on AI, engineering, and the tools I use to build reliable digital products.
          </p>
        </div>
        <Link
          to="/blog"
          className="group inline-flex w-fit items-center gap-2 text-sm text-muted transition-colors duration-300 hover:text-primary"
        >
          <span className="font-heading tracking-wide">View all articles</span>
          <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:-translate-y-0.5 group-hover:translate-x-0.5" />
        </Link>
      </div>

      {loading && latestPosts.length === 0 ? (
        <BlogPreviewSkeleton />
      ) : (
        <div className="grid grid-cols-1 gap-3 lg:grid-cols-3">
          {latestPosts.map((post) => (
            <Link
              key={post.id}
              to={getPostPath(post)}
              className="group grid grid-cols-[112px_minmax(0,1fr)] gap-4 rounded-lg border border-foreground/5 bg-card/80 p-3 transition-all duration-500 ease-out-expo hover:-translate-y-1 hover:border-primary/30 hover:bg-card hover:shadow-glow sm:grid-cols-[132px_minmax(0,1fr)] lg:grid-cols-1"
            >
              <div className="relative h-24 overflow-hidden rounded bg-foreground/5 sm:h-28 lg:h-32">
                <PostImage post={post} />
              </div>

              <div className="min-w-0 self-center py-1 lg:self-auto">
                {post.categories[0] && (
                  <span className="mb-2 block text-[10px] font-heading uppercase tracking-[0.2em] text-primary">
                    {post.categories[0]}
                  </span>
                )}

                <div className="flex items-start justify-between gap-3">
                  <h3 className="line-clamp-2 font-heading text-base font-semibold leading-snug text-foreground transition-colors duration-300 group-hover:text-primary">
                    {post.title}
                  </h3>
                  <ArrowUpRight className="mt-0.5 hidden h-4 w-4 shrink-0 text-muted transition-all duration-300 group-hover:-translate-y-0.5 group-hover:translate-x-0.5 group-hover:text-primary sm:block" />
                </div>

                <p className="mt-2 line-clamp-2 text-sm leading-relaxed text-muted">
                  {post.excerpt}
                </p>

                <div className="mt-3">
                  <PostMeta post={post} />
                </div>
              </div>
            </Link>
          ))}
        </div>
      )}
    </section>
  );
};

export default BlogPreview;
